import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Must match .speechText in AssistantDock.module.scss (0.82rem / 1.45) and the
// --dock-line custom property. Reported in whole lines rather than pixels so
// the bar only resizes on a line boundary — a few times per answer, not once
// per streamed token.
const LINE_HEIGHT_PX = 19;
const MIN_LINES = 1;
const MAX_LINES = 4;

const clampLines = (naturalHeightPx) => {
  const lines = Math.ceil(naturalHeightPx / LINE_HEIGHT_PX) || MIN_LINES;
  return Math.min(Math.max(lines, MIN_LINES), MAX_LINES);
};

// Measures how tall the current utterance wants to be, so the bar can grow with
// it instead of truncating.
const useSpeechBox = (text, onLinesChange) => {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [lines, setLines] = useState(MIN_LINES);

  const onLinesChangeRef = useRef(onLinesChange);
  onLinesChangeRef.current = onLinesChange;

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;

    const measure = () => {
      const next = clampLines(content.scrollHeight);
      setLines((current) => {
        if (current === next) return current;
        onLinesChangeRef.current?.(next);
        return next;
      });
    };

    measure();

    // Catches reflow from a window resize or a font swap, not just new text.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [text]);

  return { viewportRef, contentRef, lines };
};

// Where the box sits when new text arrives.
//
// The user's live transcript pins to the bottom: STT is real-time, so the
// newest words are the right ones. An assistant answer starts at the top and
// stays there, because it arrives in a couple of seconds while the voice takes
// twenty — anything that moves the text on its own desyncs from what is being
// said. Reading ahead is the user's call, so they scroll it themselves.
const useSpeechScroll = ({ viewportRef, text, pinToBottom }) => {
  const previousTextRef = useRef("");

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const previous = previousTextRef.current;
    previousTextRef.current = text;

    if (pinToBottom) {
      viewport.scrollTop = viewport.scrollHeight;
      return;
    }

    // Only a new utterance jumps to the top. A streamed append leaves the
    // scroll alone, so a user reading ahead is not yanked back every token.
    const isAppend = previous !== "" && text.startsWith(previous);
    if (!isAppend) viewport.scrollTop = 0;
  }, [text, pinToBottom, viewportRef]);
};

export { LINE_HEIGHT_PX, MIN_LINES, MAX_LINES, useSpeechScroll };
export default useSpeechBox;
